import { ResourceEndpoints } from '../services/crud-api.service';

const createResourceEndpoints = (base: string): ResourceEndpoints => ({
  base,
  byId: (id: string) => `${base}/${id}`
});

export const API_ENDPOINTS = {
  auth: {
    login: '/auth/login',
    completeNewPassword: '/auth/complete-new-password',
    forgotPassword: '/auth/forgot-password',
    confirmForgotPassword: '/auth/confirm-forgot-password',
    resetPassword: '/auth/reset-password',
    me: '/auth/me',
    preferences: '/auth/preferences',
    mePreferences: '/auth/me/preferences',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    refreshToken: '/auth/refresh-token'
  },
  dashboard: {
    summary: '/dashboard/summary'
  },
  users: createResourceEndpoints('/users'),
  teacherAssignments: createResourceEndpoints('/teacher-assignments'),
  academicYears: {
    ...createResourceEndpoints('/academic-years'),
    light: '/academic-years/light'
  },
  courses: createResourceEndpoints('/courses'),
  enrollments: createResourceEndpoints('/enrollments'),
  schools: createResourceEndpoints('/schools'),
  educationLevels: createResourceEndpoints('/education-levels'),
  grades: createResourceEndpoints('/grades'),
  sections: createResourceEndpoints('/sections'),
  gradeCourses: {
    base: '/grade-courses',
    bulk: '/grade-courses/bulk'
  },
  notifications: {
    base: '/notifications',
    // Recipient feed: authenticated but not permission-gated.
    me: '/notifications/me',
    // Message thread of one of the caller's own notifications. Gated by the
    // audience rule, like the feed itself — not by a NOTIFICATIONS_* permission.
    myMessages: (notificationId: string) => `/notifications/me/${notificationId}/messages`,
    // The caller's own like on one of their notifications. POST records it and
    // DELETE removes it; both answer with the resulting engagement state. Same
    // audience rule as the feed — there is no NOTIFICATIONS_* permission for it.
    myLikes: (notificationId: string) => `/notifications/me/${notificationId}/likes`,
    attachment: (notificationId: string) => `/notifications/${notificationId}/attachment`
  },
  assignments: {
    ...createResourceEndpoints('/assignments'),
    publish: (assignmentId: string) => `/assignments/${assignmentId}/publish`,
    // The homework note of every course of a section — the same number the notes
    // screen shows in its Tarea column, computed by the same function.
    homeworkScores: '/assignments/homework-scores',
    // Teacher-side files of the assignment itself (the guide, the template).
    attachments: (assignmentId: string) => `/assignments/${assignmentId}/attachments`,
    attachmentById: (assignmentId: string, attachmentId: string) =>
      `/assignments/${assignmentId}/attachments/${attachmentId}`,
    // Grading table: every enrolled student of the section, with their delivery.
    submissions: (assignmentId: string) => `/assignments/${assignmentId}/submissions`,
    submissionByStudent: (assignmentId: string, studentId: string) =>
      `/assignments/${assignmentId}/submissions/${studentId}`,
    // ONE student's delivery, and handing work in on their behalf. The narrow
    // surface a family uses: a parent may read and deliver for their own child,
    // never for the section.
    studentSubmission: (assignmentId: string, studentId: string) =>
      `/assignments/${assignmentId}/submissions/${studentId}/submission`,
    studentSubmissionAttachments: (assignmentId: string, studentId: string) =>
      `/assignments/${assignmentId}/submissions/${studentId}/submission/attachments`,
    studentSubmissionAttachmentById: (assignmentId: string, studentId: string, attachmentId: string) =>
      `/assignments/${assignmentId}/submissions/${studentId}/submission/attachments/${attachmentId}`,
    // Student surface: reading is gated by enrollment, not by a permission.
    me: '/assignments/me',
    myAssignment: (assignmentId: string) => `/assignments/me/${assignmentId}`,
    mySubmission: (assignmentId: string) => `/assignments/me/${assignmentId}/submission`,
    mySubmissionAttachments: (assignmentId: string) => `/assignments/me/${assignmentId}/submission/attachments`,
    mySubmissionAttachmentById: (assignmentId: string, attachmentId: string) =>
      `/assignments/me/${assignmentId}/submission/attachments/${attachmentId}`
  },
  notes: {
    weightSettings: '/notes/weight-settings',
    studentCourseNotes: '/notes/student-course-notes',
    studentCourseNotesByStudentId: (studentId: string) => `/notes/student-course-notes/${studentId}`,
    courseStudentAssignment: '/notes/course-student-assignment',
    // Per-student view of the notes, backing the Notes report.
    studentNotesReport: '/notes/student-notes-report',
    studentCourseNoteAssignmentById: (studentCourseNoteId: string) => `/notes/student-course-notes/${studentCourseNoteId}`
  },
  attendance: {
    calendar: '/attendance/calendar',
    exclusions: '/attendance/exclusions',
    absences: '/attendance/absences'
  },
  employees: createResourceEndpoints('/employees'),
  organizations: createResourceEndpoints('/organizations'),
  roles: {
    ...createResourceEndpoints('/roles'),
    permissionsByRoleId: (roleId: string) => `/roles/${roleId}/permissions`
  },
  permissions: createResourceEndpoints('/permissions'),
  subscriptions: {
    current: '/subscriptions/current',
    byId: (subscriptionId: string) => `/subscriptions/${subscriptionId}`,
    activate: (subscriptionId: string) => `/subscriptions/${subscriptionId}/activate`,
    cancel: (subscriptionId: string) => `/subscriptions/${subscriptionId}/cancel`,
    renew: (subscriptionId: string) => `/subscriptions/${subscriptionId}/renew`,
    payments: (subscriptionId: string) => `/subscriptions/${subscriptionId}/payments`,
    events: (subscriptionId: string) => `/subscriptions/${subscriptionId}/events`,
    organizationSubscription: (organizationId: string) => `/organizations/${organizationId}/subscription`,
    createForOrganization: (organizationId: string) => `/organizations/${organizationId}/subscriptions`,
    paymentConfirm: (paymentId: string) => `/subscription-payments/${paymentId}/confirm`,
    paymentCancel: (paymentId: string) => `/subscription-payments/${paymentId}/cancel`,
    paymentAttachments: (paymentId: string) => `/subscription-payments/${paymentId}/attachments`,
    attachmentById: (attachmentId: string) => `/subscription-payment-attachments/${attachmentId}`
  },
  // Colegiaturas: the school billing the family. Deliberately separate from the
  // `subscriptions` block above, which is Tikal billing the school.
  tuition: {
    plans: '/tuition/plans',
    planById: (planId: string) => `/tuition/plans/${planId}`,
    activatePlan: (planId: string) => `/tuition/plans/${planId}/activate`,
    planGradeAmounts: (planId: string) => `/tuition/plans/${planId}/grade-amounts`,
    gradeConfig: '/tuition/grade-config',
    generateCharges: (planId: string) => `/tuition/plans/${planId}/generate`,
    concepts: '/tuition/concepts',
    conceptById: (conceptId: string) => `/tuition/concepts/${conceptId}`,
    issueConcept: (conceptId: string) => `/tuition/concepts/${conceptId}/issue`,
    charges: '/tuition/charges',
    chargeById: (chargeId: string) => `/tuition/charges/${chargeId}`,
    chargeEvents: (chargeId: string) => `/tuition/charges/${chargeId}/events`,
    waiveCharge: (chargeId: string) => `/tuition/charges/${chargeId}/waive`,
    waiveChargeLateFee: (chargeId: string) => `/tuition/charges/${chargeId}/waive-late-fee`,
    payments: '/tuition/payments',
    paymentById: (paymentId: string) => `/tuition/payments/${paymentId}`,
    reversePayment: (paymentId: string) => `/tuition/payments/${paymentId}/reverse`,
    paymentAttachments: (paymentId: string) => `/tuition/payments/${paymentId}/attachments`,
    attachmentById: (attachmentId: string) => `/tuition/payment-attachments/${attachmentId}`,
    studentAccount: (studentId: string) => `/tuition/students/${studentId}/account`,
    studentTerm: (studentId: string) => `/tuition/students/${studentId}/term`,
    withdrawStudent: (studentId: string) => `/tuition/students/${studentId}/withdraw`,
    reinstateStudent: (studentId: string) => `/tuition/students/${studentId}/reinstate`,
    // The family view. No permission: the backend resolves it from the token
    // and the parent_students link.
    childTuition: (studentId: string) => `/parents/me/children/${studentId}/tuition`
  },
  uploads: {
    userProfilePhoto: (userId: string) => `/uploads/users/${userId}/profile-photo`,
    schoolLogo: (schoolId: string) => `/uploads/schools/${schoolId}/logo`,
    organizationLogo: (organizationId: string) => `/uploads/organizations/${organizationId}/logo`,
    gradeCover: (gradeId: string) => `/uploads/grades/${gradeId}/cover`
  }
} as const;
